import { hexLen, hexToWorld, type Hex } from '../hex.ts';
import { idiv, imax } from '../math/fixed.ts';
import { deriveSeed, rngInt, seedRng } from '../math/rng.ts';

/**
 * The cable across the abyss (docs/sendas/artifice.md, chapter 2, «El cablecarril»): the grounds, the same from the seed
 * in the sim and the drawing, integers only. Up is −y (the top of the screen), the abyss east (+x).
 *
 * - `quarryLayout` (act 1, the crane): the hanging quarry on the far face, its floor of cut terraces on the west; on its
 *   east edge the crane's foot and Dunna's winch, the arm reaching out over the abyss to the hook over the car; under the
 *   arm a scaffold of planks two rows wide going out over the drop (the far end of the arm is reached only from there).
 *   Five perches along the arm (the far three only within reach from the scaffold); blocks of cut stone on the floor.
 * - `gondolaLayout` (act 2, the abyss): the car's deck, five hexes long and three rows wide, its cabin on the east end
 *   (Dunna at its window); hand-holds along its rails and the rear's; perches on the cabin's roof and along the cable fore
 *   and aft (every one within the hero's reach from the deck: nothing can sit out of reach for good).
 * - `boxLayout` (act 3, the test): the winch house on the workshop's side, a platform of planks and stone over the drop;
 *   the windmill on the west driving the box's axle A; the box: A, B (two gears on two shafts chained together, so they
 *   turn as one: B1 meshes with A, B2 with C), C (to the drum), the loose axle R beside B2 and C; the drum on the east
 *   with the cable going out over the abyss, La Picotera on it; the shelf of gears along the south; the clutch lever.
 *   `boxTrain` turns a setting into speeds and ways, `boxSolve` counts the right ones.
 */

export const QUARRY_RADIUS = 8;
export const GONDOLA_RADIUS = 6;
export const BOX_RADIUS = 7;

const allCells = (R: number): Hex[] => {
  const out: Hex[] = [];
  for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (hexLen(q, r) <= R) out.push({ q, r });
  return out;
};
const key = (h: Hex): string => `${h.q},${h.r}`;
const wx = (h: Hex): number => hexToWorld(h.q, h.r).x;

/* ---------- the crane ---------- */

export interface QuarryLayout {
  radius: number;
  rocks: Hex[];
  /** For the drawing: the quarry's floor, the scaffold's planks, the blocks of cut stone, the crane's foot. */
  floor: Hex[];
  scaffold: Hex[];
  blocks: Hex[];
  foot: { x: number; y: number };
  winch: { x: number; y: number };
  hook: { x: number; y: number };
  /** Past this x on the arm a perched gargoyle weighs double (the long end of the lever). */
  heavyX: number;
  /** Along the arm, from its foot out to the hook. */
  perches: { x: number; y: number }[];
  /** Where the swaying piece sweeps (over the scaffold's tip). */
  swingAt: { x: number; y: number };
  start: { x: number; y: number };
  from: { x: number; y: number };
}

export const quarryLayout = (seed: number): QuarryLayout => {
  const R = QUARRY_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0xc4a3));
  const foot: Hex = { q: 1, r: -2 };
  const scaffold = allCells(R).filter((h) => (h.r === -1 && h.q >= 2 && h.q <= 6) || (h.r === 0 && h.q >= 2 && h.q <= 5));
  const sc = new Set(scaffold.map(key));
  const base = allCells(R).filter((h) => hexLen(h.q, h.r) <= R - 1 && wx(h) <= 1000 && !(h.q === foot.q && h.r === foot.r));
  // Blocks of cut stone on the floor, west of the winch, apart (cover, and the quarry's look).
  const blocks: Hex[] = [];
  const spots = base.filter((h) => wx(h) <= -1500 && hexLen(h.q, h.r) <= R - 2 && Math.abs(h.r) >= 1);
  for (let g = 0; g < 80 && blocks.length < 5; g++) {
    const h = spots[rngInt(rng, spots.length)] as Hex;
    if (blocks.some((o) => hexLen(o.q - h.q, o.r - h.r) < 3)) continue;
    blocks.push(h);
  }
  const bl = new Set(blocks.map(key));
  const floor = base.filter((h) => !bl.has(key(h)));
  const open = new Set([...floor.map(key), ...sc]);
  const rocks = allCells(R + 2).filter((h) => !open.has(key(h)));
  const fp = hexToWorld(foot.q, foot.r);
  const armY = fp.y;
  return {
    radius: R,
    rocks,
    floor,
    scaffold,
    blocks,
    foot: fp,
    winch: hexToWorld(0, -1),
    hook: { x: 6200, y: armY },
    heavyX: 4000,
    perches: [1500, 2600, 3700, 4800, 5900].map((x) => ({ x, y: armY })),
    swingAt: { x: 5200, y: -650 },
    start: { x: -1500, y: 300 },
    from: { x: 10500, y: -6000 },
  };
};

/* ---------- the car over the abyss ---------- */

export interface GondolaLayout {
  radius: number;
  rocks: Hex[];
  deck: Hex[];
  cabin: Hex[];
  holds: { x: number; y: number }[];
  roof: { x: number; y: number }[];
  cable: { x: number; y: number }[];
  dunnaAt: { x: number; y: number };
  start: { x: number; y: number };
  from: { x: number; y: number };
}

export const gondolaLayout = (seed: number): GondolaLayout => {
  void seed;
  const R = GONDOLA_RADIUS;
  const deck = allCells(R).filter((h) => (h.r === 0 && h.q >= -3 && h.q <= 1) || (h.r === -1 && h.q >= -2 && h.q <= 1) || (h.r === 1 && h.q >= -3 && h.q <= 0));
  const cabin = [
    { q: 2, r: -1 },
    { q: 2, r: 0 },
    { q: 1, r: 1 },
  ];
  const open = new Set(deck.map(key));
  const rocks = allCells(R + 2).filter((h) => !open.has(key(h)));
  return {
    radius: R,
    rocks,
    deck,
    cabin,
    holds: [
      { x: -2600, y: -1000 },
      { x: -2600, y: 1000 },
      { x: -1000, y: -1000 },
      { x: -1000, y: 1000 },
      { x: 700, y: -1000 },
      { x: 700, y: 1000 },
      { x: -3450, y: 0 },
      { x: 1150, y: 0 },
    ],
    roof: [
      { x: 1700, y: -450 },
      { x: 2300, y: 0 },
      { x: 1700, y: 450 },
    ],
    // Along the cable: aft over the drop, over the deck, fore past the cabin (all within reach of the deck's ends).
    cable: [-5600, -4500, -3400, -1500, 0, 2800, 3700].map((x) => ({ x, y: 0 })),
    dunnaAt: { x: 2000, y: 0 },
    start: { x: -1000, y: 0 },
    from: { x: -2000, y: -9500 },
  };
};

/* ---------- the gearbox ---------- */

/** The teeth the shelf's gears come in. */
export const BOX_TEETH = [12, 18, 24, 36];
/** The motor's (the windmill's) turns a minute on axle A. */
export const BOX_IN = 60;
/** The box's slots by index. */
export const BOX_A = 0;
export const BOX_B1 = 1;
export const BOX_B2 = 2;
export const BOX_C = 3;
export const BOX_R = 4;

export interface BoxLayout {
  radius: number;
  rocks: Hex[];
  floor: Hex[];
  /** A, B1, B2, C, R; the gears' lying spots on the shelf (one each); the clutch lever. */
  slots: Hex[];
  lie: Hex[];
  lever: Hex;
  /** The drum (east, the cable going out from it), the windmill (west). */
  drum: { x: number; y: number };
  mill: { x: number; y: number };
  pieces: number[];
  want: number;
  way: number;
  bossAt: { x: number; y: number };
  dunnaAt: { x: number; y: number };
  start: { x: number; y: number };
  from: { x: number; y: number };
}

export interface BoxTrain {
  /** Each slot's speed in tenths of a turn a minute (0: still) and its way (+1 the motor's, −1 the other, 0 still). */
  speed: number[];
  dir: number[];
  /** A, B1, B2 and C all set: the drum is driven. */
  full: boolean;
}

/** A setting's speeds and ways (teeth on A, B1, B2, C, R; 0 empty). B1 and B2 turn together (their shafts are chained). */
export const boxTrain = (set: number[]): BoxTrain => {
  const [a = 0, b1 = 0, b2 = 0, c = 0, r = 0] = set;
  const speed = [0, 0, 0, 0, 0];
  const dir = [0, 0, 0, 0, 0];
  // In tenths, rounded to the nearest (n / d + ½).
  const tenth = (n: number, d: number): number => idiv(n * 2 + d, d * 2);
  if (a) {
    speed[BOX_A] = BOX_IN * 10;
    dir[BOX_A] = 1;
  }
  const bTurns = a > 0 && b1 > 0;
  if (bTurns) {
    speed[BOX_B1] = tenth(BOX_IN * 10 * a, b1);
    dir[BOX_B1] = -1;
    if (b2) {
      speed[BOX_B2] = speed[BOX_B1] as number;
      dir[BOX_B2] = -1;
    }
  }
  if (bTurns && b2 && r) {
    speed[BOX_R] = tenth(BOX_IN * 10 * a * b2, b1 * r);
    dir[BOX_R] = 1;
  }
  if (bTurns && b2 && c) {
    speed[BOX_C] = tenth(BOX_IN * 10 * a * b2, b1 * c);
    dir[BOX_C] = r ? -1 : 1;
  }
  return { speed, dir, full: !!(a && b1 && b2 && c) };
};

/** Whether a setting turns the drum at `want` (whole turns a minute, exactly) and its way. */
export const boxRight = (set: number[], want: number, way: number): boolean => {
  const [a = 0, b1 = 0, b2 = 0, c = 0, r = 0] = set;
  if (!a || !b1 || !b2 || !c) return false;
  return BOX_IN * a * b2 === want * b1 * c && (r ? -1 : 1) === way;
};

/**
 * Every right setting of a shelf (distinct by the teeth on A, B1, B2, C): R gets a gear left over when the way asks for
 * it (its teeth don't change the speed), none when it doesn't.
 */
export const boxSolve = (pieces: number[], want: number, way: number): number[][] => {
  const out: number[][] = [];
  const seen = new Set<string>();
  const n = pieces.length;
  for (let i = 0; i < n; i++)
    for (let j = 0; j < n; j++)
      for (let k = 0; k < n; k++)
        for (let l = 0; l < n; l++) {
          if (new Set([i, j, k, l]).size < 4) continue;
          const set = [pieces[i], pieces[j], pieces[k], pieces[l]] as number[];
          let rest = -1;
          if (way < 0) {
            for (let m = 0; m < n && rest < 0; m++) if (m !== i && m !== j && m !== k && m !== l) rest = m;
            if (rest < 0) continue;
          }
          const full = [...set, rest >= 0 ? (pieces[rest] as number) : 0];
          if (!boxRight(full, want, way)) continue;
          const id = set.join(',');
          if (seen.has(id)) continue;
          seen.add(id);
          out.push(full);
        }
  return out;
};

/**
 * Which drum speeds a shelf can give for a way, with how many right settings each (whole turns only), and whether any
 * of them uses the shelf's biggest gear on C (the obvious: brake with the biggest at the end) or the same on B1 and B2
 * (one stage only).
 */
const speedsOf = (pieces: number[], way: number): Map<number, { n: number; obvious: boolean; flat: boolean }> => {
  const out = new Map<number, { n: number; obvious: boolean; flat: boolean }>();
  const seen = new Set<string>();
  const big = pieces.reduce((m, x) => imax(m, x), 0);
  const n = pieces.length;
  for (let i = 0; i < n; i++)
    for (let j = 0; j < n; j++)
      for (let k = 0; k < n; k++)
        for (let l = 0; l < n; l++) {
          if (new Set([i, j, k, l]).size < 4) continue;
          if (way < 0 && n < 5) continue;
          const [a, b1, b2, c] = [pieces[i], pieces[j], pieces[k], pieces[l]] as number[];
          const id = `${a},${b1},${b2},${c}`;
          if (seen.has(id)) continue;
          seen.add(id);
          const num = BOX_IN * (a as number) * (b2 as number);
          const den = (b1 as number) * (c as number);
          if (num % den !== 0) continue;
          const v = idiv(num, den);
          const o = out.get(v) ?? { n: 0, obvious: false, flat: false };
          o.n++;
          if (c === big) o.obvious = true;
          if (b1 === b2) o.flat = true;
          out.set(v, o);
        }
  return out;
};

/**
 * The winch house from the seed: the shelf and what the drum asks. Kept only if one or two settings turn it right, none
 * of them with the biggest gear on C (the obvious brake) nor the same gear on B1 and B2 (the ratio comes from
 * multiplying two stages), and not 60 (the motor's own).
 */
export const boxLayout = (seed: number): BoxLayout => {
  const R = BOX_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0xb0c5));
  const slots: Hex[] = [
    { q: -4, r: 0 },
    { q: -3, r: 1 },
    { q: -1, r: 1 },
    { q: 1, r: 0 },
    { q: 0, r: 2 },
  ];
  const lie: Hex[] = [
    { q: -6, r: 3 },
    { q: -4, r: 3 },
    { q: -2, r: 3 },
    { q: 0, r: 3 },
    { q: 2, r: 3 },
  ];
  const lever: Hex = { q: 2, r: 1 };
  const drumH: Hex = { q: 3, r: 0 };
  const millH: Hex = { q: -6, r: 0 };
  let pieces: number[] = [12, 24, 24, 36, 18];
  let want = 45;
  let way = -1;
  for (let tries = 0; tries < 600; tries++) {
    const p = Array.from({ length: 5 }, () => BOX_TEETH[rngInt(rng, BOX_TEETH.length)] as number);
    if (new Set(p).size < 3) continue;
    const w = rngInt(rng, 2) === 0 ? 1 : -1;
    const ok = [...speedsOf(p, w)].filter(([v, o]) => v >= 10 && v <= 180 && v !== BOX_IN && o.n >= 1 && o.n <= 2 && !o.obvious && !o.flat);
    if (!ok.length) continue;
    ok.sort((x, y) => x[0] - y[0]);
    const [v] = ok[rngInt(rng, ok.length)] as [number, unknown];
    if (boxSolve(p, v, w).length < 1) continue;
    pieces = p;
    want = v;
    way = w;
    break;
  }
  const solid = new Set([key(drumH), key(millH)]);
  const floor = allCells(R).filter((h) => hexLen(h.q, h.r) <= R - 1 && wx(h) <= 4200 && !solid.has(key(h)));
  const open = new Set(floor.map(key));
  const rocks = allCells(R + 2).filter((h) => !open.has(key(h)));
  const drum = hexToWorld(drumH.q, drumH.r);
  return {
    radius: R,
    rocks,
    floor,
    slots,
    lie,
    lever,
    drum,
    mill: hexToWorld(millH.q, millH.r),
    pieces,
    want,
    way,
    bossAt: { x: drum.x, y: drum.y - 450 },
    dunnaAt: hexToWorld(-2, -2),
    start: { x: -2000, y: -700 },
    from: { x: 11000, y: -5000 },
  };
};
